import * as csstree from 'css-tree';
import Api from './Api';
import { getMediaSize } from './media';

export type ThemeRules = {
    selector: string;
    style: {
        name: string;
        value: string;
    }[];
}[];

let theme: ThemeRules = [];
let themeStyle: HTMLStyleElement | null = null;
let layoutCallback: () => void = () => {};
const themeChangeListeners = new Set<() => void>();
let themePersistedCallback: ((theme: ThemeRules) => void) | undefined;
let fontsLoaded = ['Inter', 'Roboto'];

type ThemePreviewOverride = { element: HTMLElement; name: string };
let themePreviewOverrides: ThemePreviewOverride[] = [];

const applyFonts = () => {
    const names = [
        '--body-font-family',
        '--headline-font-family',
        '--menu-font-family',
        '--button-font-family',
        '--font-import'
    ];
    names.forEach(name => {
        const fonts = getThemeValue('BODY', name);
        if (fonts) {
            fonts.split(',').forEach(font => {
                font = font.trim();
                if (!fontsLoaded.includes(font)) {
                    const fontLink = document.createElement('link');
                    fontLink.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
                    fontLink.rel = 'stylesheet';
                    document.head.appendChild(fontLink);
                    fontsLoaded.push(font);
                }
            });
        }
    });
};

function clearThemePreviewOverrides() {
    for (const { element, name } of themePreviewOverrides) {
        element.style.removeProperty(name);
    }
    themePreviewOverrides = [];
}

function getThemeTargetElement(selector: string): HTMLElement | null {
    if (selector === 'BODY' || selector === 'body') {
        return document.body;
    }
    if (selector.startsWith('BODY.')) {
        const mode = selector.slice('BODY.'.length);
        if (document.body.classList.contains(mode)) {
            return document.body;
        }
    }
    return null;
}

/** Live preview without rebuilding the full brand stylesheet (important for large custom themes). */
function setThemePreviewOverride(selector: string, name: string, value: string): boolean {
    const element = getThemeTargetElement(selector);
    if (!element) {
        return false;
    }

    themePreviewOverrides = themePreviewOverrides.filter(
        override => !(override.element === element && override.name === name)
    );

    if (value === '') {
        element.style.removeProperty(name);
    } else {
        element.style.setProperty(name, value);
        themePreviewOverrides.push({ element, name });
    }

    return true;
}

function mutateThemeValue(selector: string, name: string, value: string) {
    let node = theme.find(t => t.selector === selector);
    if (!node) {
        node = { selector, style: [] };
        theme.push(node);
    }
    let style = node.style.find(s => s.name === name);
    if (value === '') {
        if (style) {
            node.style.splice(node.style.indexOf(style), 1);
        }
    } else {
        if (!style) {
            style = { name, value };
            node.style.push(style);
        } else {
            style.value = value;
        }
    }
    if (node.style.length === 0) {
        theme.splice(theme.indexOf(node), 1);
    }
}

export const applyTheme = () => {
    clearThemePreviewOverrides();
    let css = getThemeText();
    const priLight = parseColorToRGBA(getThemeValue('BODY.light', '--primary-main') || '#1565C0');
    if (priLight) {
        const lighten = {
            r: Math.min(255, Math.round(priLight.r * 1.2)),
            g: Math.min(255, Math.round(priLight.g * 1.2)),
            b: Math.min(255, Math.round(priLight.b * 1.2)),
            a: priLight.a
        };
        const darken = {
            r: Math.max(0, Math.round(priLight.r * 0.8)),
            g: Math.max(0, Math.round(priLight.g * 0.8)),
            b: Math.max(0, Math.round(priLight.b * 0.8)),
            a: priLight.a
        };
        css +=
            '\n\n' +
            'BODY.light {\n' +
            '  --mui-palette-primary-main: ' +
            color2hex(priLight) +
            ';\n' +
            '  --mui-palette-primary-light: ' +
            color2hex(lighten) +
            ';\n' +
            '  --mui-palette-primary-dark: ' +
            color2hex(darken) +
            ';\n' +
            '  --mui-palette-primary-mainChannel: ' +
            priLight.r +
            ', ' +
            priLight.g +
            ', ' +
            priLight.b +
            ';\n' +
            '  --mui-palette-primary-lightChannel: ' +
            lighten.r +
            ', ' +
            lighten.g +
            ', ' +
            lighten.b +
            ';\n' +
            '  --mui-palette-primary-darkChannel: ' +
            darken.r +
            ', ' +
            darken.g +
            ', ' +
            darken.b +
            ';\n' +
            '}';
    }
    const priDark = parseColorToRGBA(getThemeValue('BODY.dark', '--primary-main') || '#1565C0');
    if (priDark) {
        const lighten = {
            r: Math.min(255, Math.round(priDark.r * 1.2)),
            g: Math.min(255, Math.round(priDark.g * 1.2)),
            b: Math.min(255, Math.round(priDark.b * 1.2)),
            a: priDark.a
        };
        const darken = {
            r: Math.max(0, Math.round(priDark.r * 0.8)),
            g: Math.max(0, Math.round(priDark.g * 0.8)),
            b: Math.max(0, Math.round(priDark.b * 0.8)),
            a: priDark.a
        };
        css +=
            '\n\n' +
            'BODY.dark {\n' +
            '  --mui-palette-primary-main: ' +
            color2hex(priDark) +
            ';\n' +
            '  --mui-palette-primary-light: ' +
            color2hex(lighten) +
            ';\n' +
            '  --mui-palette-primary-dark: ' +
            color2hex(darken) +
            ';\n' +
            '  --mui-palette-primary-mainChannel: ' +
            priDark.r +
            ', ' +
            priDark.g +
            ', ' +
            priDark.b +
            ';\n' +
            '  --mui-palette-primary-lightChannel: ' +
            lighten.r +
            ', ' +
            lighten.g +
            ', ' +
            lighten.b +
            ';\n' +
            '  --mui-palette-primary-darkChannel: ' +
            darken.r +
            ', ' +
            darken.g +
            ', ' +
            darken.b +
            ';\n' +
            '}';
    }
    if (themeStyle) {
        if (themeStyle.isConnected) {
            themeStyle.remove();
        }
        themeStyle = null;
    }
    themeStyle = document.createElement('style');
    themeStyle.type = 'text/css';
    themeStyle.appendChild(document.createTextNode(css));
    document.head.appendChild(themeStyle);
    applyFonts();
};

export function color2hex(color: any) {
    return (
        '#' +
        color.r.toString(16).padStart(2, '0') +
        color.g.toString(16).padStart(2, '0') +
        color.b.toString(16).padStart(2, '0') +
        color.a.toString(16).padStart(2, '0')
    );
}

export const getCssValue = (name: string): string => {
    const temp = document.createElement('div');
    document.body.appendChild(temp);
    temp.style.cssText = 'color: ' + name + ';';
    const computed = window.getComputedStyle(temp);
    const renderedColor = computed.color;
    document.body.removeChild(temp);
    return renderedColor;
};

export const getTheme = () => theme;

export const getThemeText = () => {
    return theme
        .map(
            t => `${t.selector} {\n  ${t.style.map(s => `${s.name}: ${s.value};`).join('\n  ')}\n}`
        )
        .join('\n\n');
};

export const getThemeValue = (selector: string, name: string, fallback?: string) => {
    return (
        theme.find(t => t.selector === selector)?.style.find(s => s.name === name)?.value ??
        fallback
    );
};

export const parseColorToRGBA = (function () {
    const cache: any = {}; // For memoization
    return function (color: string) {
        if (cache[color]) {
            return cache[color];
        }

        const el = document.createElement('div');
        el.style.color = color;
        document.body.appendChild(el);
        const computed = window.getComputedStyle(el).color;
        document.body.removeChild(el);

        let match = computed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/i);
        if (match) {
            const r = parseInt(match[1], 10);
            const g = parseInt(match[2], 10);
            const b = parseInt(match[3], 10);
            const a = match[4] ? Math.round(parseFloat(match[4]) * 255) : 255;
            const result = { r, g, b, a };
            cache[color] = result;
            return result;
        }

        match = computed.match(
            /^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\)$/i
        );
        if (match) {
            const r = Math.round(parseFloat(match[1]) * 255);
            const g = Math.round(parseFloat(match[2]) * 255);
            const b = Math.round(parseFloat(match[3]) * 255);
            const a = match[4] ? Math.round(parseFloat(match[4]) * 255) : 255;
            const result = { r, g, b, a };
            cache[color] = result;
            return result;
        }

        return null;
    };
})();

export const parseThemeText = (t: string): ThemeRules => {
    try {
        var ast = csstree.parse(t, { context: 'stylesheet' });
        const rules: any = [];
        csstree.walk(ast, {
            visit: 'Rule',
            enter(node: any) {
                rules.push({
                    selector: csstree.generate(node.prelude),
                    style: node.block.children
                        .toArray()
                        .filter((c: any) => c.type === 'Declaration')
                        .map((node: any) => {
                            return {
                                name: node.property,
                                value: String(csstree.generate(node.value)).trim()
                            };
                        })
                });
            }
        });
        return rules;
    } catch (e) {}
    return [];
};

let themeSaveInFlight = false;
let themeSaveQueued = false;

/**
 * Saves are serialized to one in-flight PATCH at a time. If edits land while a save is
 * already out, we just mark it queued and let that in-flight request's completion kick
 * off the next one (always reading the *current* `theme`) — this stops an earlier,
 * slower request from resolving after a later one and silently overwriting the newer
 * value (out-of-order completion).
 */
function saveTheme() {
    themePersistedCallback?.(theme);
    themeSaveQueued = true;
    if (themeSaveInFlight) {
        return;
    }
    flushThemeSave();
}

// Chrome caps keepalive request bodies at ~64KB combined across all in-flight keepalive
// fetches; stay well under that so an oversized custom theme doesn't fail to save outright.
const KEEPALIVE_BODY_LIMIT = 60_000;

function flushThemeSave() {
    if (!themeSaveQueued) {
        return;
    }
    themeSaveQueued = false;
    themeSaveInFlight = true;
    const payload = { theme };
    // keepalive lets the browser finish sending this even if the tab navigates/reloads
    // right after the user commits a change — otherwise the in-flight PATCH is aborted
    // by the unload and the edit is silently lost.
    const keepalive = JSON.stringify(payload).length < KEEPALIVE_BODY_LIMIT;
    Api.patch('brands', payload, undefined, keepalive ? { keepalive: true } : undefined)
        .catch(error => {
            console.error('Failed to save brand theme', error);
        })
        .finally(() => {
            themeSaveInFlight = false;
            flushThemeSave();
        });
}

/** True while a brand theme save is in flight or queued — used to warn before an unload would lose it. */
export function isThemeSavePending(): boolean {
    return themeSaveInFlight || themeSaveQueued;
}

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', event => {
        if (isThemeSavePending()) {
            event.preventDefault();
            event.returnValue = '';
        }
    });
}

/** Register a callback to sync host/brand caches after theme saves from the Branding UI. */
export function setThemePersistedCallback(callback: ((theme: ThemeRules) => void) | undefined) {
    themePersistedCallback = callback;
}

export const setTheme = (t: string | ThemeRules, save?: boolean) => {
    if (typeof t === 'string') {
        t = parseThemeText(t);
    }
    theme = t;
    applyTheme();
    if (save) {
        saveTheme();
    }
};

export const setThemeValue = (selector: string, name: string, value: string, save?: boolean) => {
    mutateThemeValue(selector, name, value);
    if (save !== false) {
        applyTheme();
        saveTheme();
        return;
    }
    if (!setThemePreviewOverride(selector, name, value)) {
        applyTheme();
    }
};

export const updateIcon = () => {
    [16, 32, 48, 96, 192, 512].forEach((i: number) => {
        const img = Api.hostInfo?.favIcon
            ? getMediaSize(Api.hostInfo.favIcon, { width: i, height: i, noPix: true })
            : null;
        if (img) {
            const href =
                img.url +
                (img.url.indexOf('?') === -1 ? '?w=' + i + '&h=' + i : '') +
                '&fit=pad&bg=white';
            let link = document.querySelector("link[rel*='icon'][sizes*='" + i + 'x' + i + "']");
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'icon');
                link.setAttribute('sizes', `${i}x${i}`);
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        }
    });
};

export const updateLayout = () => {
    layoutCallback?.();
};

export const updateLayoutCallback = (fn: () => void) => {
    layoutCallback = fn;
};

/** Subscribe to theme updates applied outside the Branding editor (e.g. AI assistant). */
export function subscribeThemeChange(listener: () => void): () => void {
    themeChangeListeners.add(listener);
    return () => {
        themeChangeListeners.delete(listener);
    };
}

function notifyThemeChange() {
    themeChangeListeners.forEach(listener => listener());
}

/** Apply theme rules from the brand API without re-saving; refreshes page CSS and notifies UI. */
export function syncThemeFromBrand(rules: ThemeRules, notify = true) {
    setTheme(rules, false);
    if (notify) {
        notifyThemeChange();
    }
}
